import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SeahorseTemplateDirective } from '@seahorse/common';
import {
  CellClickEventArgs,
  DayService,
  EventClickArgs,
  EventSettingsModel,
  MonthService,
  PopupOpenEventArgs,
  ScheduleModule,
  WeekService,
  WorkWeekService,
  ScheduleComponent,
  ActionEventArgs,
  TimelineViewsService,
  AgendaService,
} from '@syncfusion/ej2-angular-schedule';
import { Subscription } from 'rxjs';
import {
  CalendarActionEvent,
  CalendarConfig,
  CalendarData,
} from './calendar.model';
import { loadTranslations } from './localization/translation-loader';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

@Component({
  selector: 'sh-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ScheduleModule, TranslateModule],
  providers: [
    DayService,
    WeekService,
    WorkWeekService,
    MonthService,
    TimelineViewsService,
    AgendaService,
  ],
})
export class CalendarComponent
  implements AfterViewInit, AfterContentInit, OnDestroy
{
  @ViewChild(ScheduleComponent) scheduleRef?: ScheduleComponent;
  @ViewChild(ScheduleComponent, { read: ViewContainerRef })
  scheduleVcRef?: ViewContainerRef;

  @ContentChildren(SeahorseTemplateDirective)
  templates?: QueryList<SeahorseTemplateDirective>;

  private _data?: CalendarData;
  @Input() set data(val: CalendarData | undefined) {
    this._data = val;
    this.initData();
  }
  get data(): CalendarData | undefined {
    return this._data;
  }

  private _config?: CalendarConfig;
  @Input() set config(val: CalendarConfig | undefined) {
    if (!val) {
      return;
    }

    this._config = val;
    this.setUpConfig(val);
  }

  get config() {
    return this._config;
  }

  @Output() action = new EventEmitter<CalendarActionEvent>();
  @Output() dateChange = new EventEmitter<Date[]>();

  settings: EventSettingsModel = {};

  get startingDay(): number {
    return this.scheduleRef && this.scheduleRef?.currentView === 'Week'
      ? new Date().getDay()
      : 1;
  }

  private _subscriptions = new Subscription();
  eventDetailsTemplate: TemplateRef<unknown> | null = null;
  eventFormTemplate: TemplateRef<unknown> | null = null;
  locale = this._translateService.getDefaultLang();

  constructor(private _translateService: TranslateService) {
    const lang = _translateService.currentLang ?? _translateService.defaultLang;
    loadTranslations(lang).then(() => (this.locale = lang));
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.config?.initPrevious) {
        const previousButton = document.querySelector(
          '.e-prev > button'
        ) as HTMLButtonElement;
        previousButton.click();
      }

      this.emitDateChanged();
    });
  }

  ngAfterContentInit() {
    this.setTemplates(this.templates);

    this._subscriptions.add(
      this.templates?.changes.subscribe(
        (templates: QueryList<SeahorseTemplateDirective>) =>
          this.setTemplates(templates)
      )
    );
  }

  private setTemplates(templates?: QueryList<SeahorseTemplateDirective>) {
    templates?.forEach((template) => {
      switch (template.type) {
        case 'eventForm':
          this.eventFormTemplate = template.templateRef;
          break;
        case 'eventDetails':
          this.eventDetailsTemplate = template.templateRef;
          break;
        default:
          break;
      }
    });
  }

  initData() {
    this.settings = {
      ...this.settings,
      dataSource: this.data,
    };

    this.scheduleVcRef?.injector.get(ChangeDetectorRef).detectChanges();
    this.scheduleRef?.refreshEvents();
  }

  setUpConfig(config: CalendarConfig) {
    const { titleField, startField, endField, idField } = config;

    this.settings = {
      ...this.settings,
      fields: {
        id: idField,
        subject: { name: titleField },
        startTime: { name: startField },
        endTime: { name: endField },
      },
    };
  }

  onCellClick(args: CellClickEventArgs) {
    const action = args.event?.type;

    if (action === 'dblclick') {
      args.cancel = true;
    }
  }

  onPopupOpen($event: PopupOpenEventArgs) {
    const data = this.mapEventToData($event.data);
    this.action.emit({ action: 'openEventForm', data });
  }

  onPopupClose() {
    this.action.emit({ action: 'openEventForm', data: null });
  }

  onEventClick($event: EventClickArgs) {
    this.action.emit({ action: 'selectEvent', data: $event.event });
  }

  onAdd(data: unknown) {
    this.action.emit({ action: 'addEvent', data });
  }

  onDelete(data: unknown) {
    this.action.emit({ action: 'deleteEvent', data });
  }

  openDetails(data: unknown) {
    this.action.emit({ action: 'openDetails', data });
  }

  onActionComplete($event: ActionEventArgs) {
    if (['dateNavigate', 'viewNavigate'].includes($event.requestType)) {
      this.emitDateChanged();
    }
  }

  closePopup() {
    this.scheduleRef?.closeQuickInfoPopup();
    this.onPopupClose();
  }

  private emitDateChanged() {
    const dates = this.scheduleRef?.activeView.renderDates;

    if (!dates) {
      return;
    }

    const start = dates[0];
    const end = dates[dates.length - 1];

    this.dateChange.emit([start, end]);
  }

  private mapEventToData(event?: Record<string, unknown>) {
    if (!event) {
      return null;
    }

    if (!this.config) {
      return event;
    }

    const { titleField, startField, endField, idField } = this.config;
    const { id, subject, startTime, endTime } = event;

    return {
      ...event,
      [titleField]: subject,
      [startField]: startTime
        ? moment(startTime).format('YYYY-MM-DDTHH:mm')
        : null,
      [endField]: endTime ? moment(endTime).format('YYYY-MM-DDTHH:mm') : null,
      [idField]: id,
    };
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
